import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, todayYmd } from '@/lib/officialsApi';
import { BATCH_STATUS, Field, Loading, Money, Panel, PayStatusBadge } from './PayBits';
import { BatchDetailDialog } from './BatchDetailDialog';

const monthStart = () => `${todayYmd().slice(0, 8)}01`;

const CreateBatchDialog = ({ open, orgId, competitions, onClose, onCreated }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: '', from_date: monthStart(), to_date: todayYmd(), event_id: '' }); }, [open]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await oapi('/officials/payments/batches', { method: 'POST', orgId, body: { ...form, event_id: form.event_id || null } });
      toast.success(`Batch created · ${r.batch.officials.length} official${r.batch.officials.length === 1 ? '' : 's'}`);
      onCreated(r.batch);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="batch-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">New payment batch</DialogTitle>
          <DialogDescription>Collects every verified or approved-but-unpaid item in the period that isn't already in a batch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name (optional)"><Input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. October payroll" data-testid="batch-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><Input type="date" value={form.from_date || ''} onChange={e => set('from_date', e.target.value)} data-testid="batch-from" /></Field>
            <Field label="To"><Input type="date" value={form.to_date || ''} onChange={e => set('to_date', e.target.value)} data-testid="batch-to" /></Field>
          </div>
          <Field label="League / tournament">
            <Select value={form.event_id || 'all'} onValueChange={v => set('event_id', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="batch-league"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All leagues & tournaments</SelectItem>{competitions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !form.from_date || !form.to_date} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="batch-create-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Create batch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BatchRow = ({ b, currency, onOpen }) => (
  <button type="button" onClick={() => onOpen(b.id)} className="w-full text-left flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-t first:border-t-0 hover:bg-slate-50 transition-colors" data-testid={`batch-row-${b.id}`}>
    <div className="flex-1 min-w-[200px]">
      <div className="font-bold text-slate-900">{b.name}</div>
      <div className="text-xs text-slate-500">{b.from_date} → {b.to_date} · {b.officials.length} official{b.officials.length === 1 ? '' : 's'} · {b.games} game{b.games === 1 ? '' : 's'} · by {b.created_by_name}</div>
    </div>
    <PayStatusBadge status={b.display_status} map={BATCH_STATUS} testId={`batch-status-${b.id}`} />
    <div className="text-right w-28"><div className="text-[10px] uppercase font-bold text-slate-400">Total</div><Money minor={b.total_minor} currency={currency} className="font-bold" /></div>
    <div className="text-right w-28"><div className="text-[10px] uppercase font-bold text-slate-400">Remaining</div><Money minor={b.remaining_minor} currency={currency} className="font-bold text-red-600" testId={`batch-remaining-${b.id}`} /></div>
  </button>
);

export const BatchesPanel = ({ orgId, currency, perms, competitions, tick, refresh }) => {
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => { oapi('/officials/payments/batches', { orgId }).then(d => setList(d.batches)).catch(e => toast.error(e.message)); }, [orgId]);
  useEffect(() => { load(); }, [load, tick]);
  return (
    <div className="space-y-4" data-testid="pay-batches">
      <Panel title="Payment batches" desc="Group a pay period, review it, approve it in one step, then record every payment at once." testId="batches-panel"
        actions={perms.has('approve') && <Button size="sm" onClick={() => setCreating(true)} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="batch-create-btn"><Plus className="w-4 h-4 mr-1.5" />New batch</Button>}>
        {!list ? <Loading /> : !list.length ? (
          <div className="py-12 text-center" data-testid="batches-empty"><Layers className="w-8 h-8 mx-auto text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No batches yet. Create one to pay a whole period at once.</p></div>
        ) : list.map(b => <BatchRow key={b.id} b={b} currency={currency} onOpen={setOpenId} />)}
      </Panel>
      <CreateBatchDialog open={creating} orgId={orgId} competitions={competitions} onClose={() => setCreating(false)}
        onCreated={(b) => { setCreating(false); refresh(); setOpenId(b.id); }} />
      <BatchDetailDialog batchId={openId} orgId={orgId} currency={currency} perms={perms} onClose={() => setOpenId(null)} onChanged={refresh} />
    </div>
  );
};
